import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Filter, Trash2, Plus, ToggleLeft, ToggleRight } from 'lucide-react';

// Phase 5 UI — Rules Engine. Doctrine: permanent, versioned, apply-on-scan.
// Predicate is intentionally simple: {field, op, value}. The apply is server-side.

const FIELDS = [
  { key: 'latest_price',     label: 'Price' },
  { key: 'normalized_title', label: 'Title contains' },
  { key: 'source_key',       label: 'Source' },
  { key: 'location_text',    label: 'Location text' },
];

const OPS_BY_FIELD = {
  latest_price:     [['>', 'greater than'], ['>=', '≥'], ['<', 'less than'], ['<=', '≤'], ['==', 'equals']],
  normalized_title: [['contains', 'contains'], ['not_contains', 'does NOT contain']],
  source_key:       [['==', 'is'], ['!=', 'is not']],
  location_text:    [['contains', 'contains'], ['not_contains', 'does NOT contain']],
};

const ACTIONS = [
  { key: 'hide',         label: 'Hide from feed' },
  { key: 'flag_review',  label: 'Flag for review' },
  { key: 'always_show',  label: 'Always surface' },
];

const Rules = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [field, setField] = useState('latest_price');
  const [op, setOp] = useState('>');
  const [value, setValue] = useState('');
  const [action, setAction] = useState('hide');
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/rules');
      setRules(r.data.rules || []);
    } catch (e) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const ops = OPS_BY_FIELD[field] || [];
    if (ops.length && !ops.map((o) => o[0]).includes(op)) setOp(ops[0][0]);
  }, [field, op]);

  const save = async (e) => {
    e.preventDefault();
    if (!label.trim() || !value.toString().trim()) {
      toast.error('Give the rule a name and a value.');
      return;
    }
    let coerced = value;
    if (field === 'latest_price') coerced = Number(value);
    try {
      await api.post('/rules', {
        label: label.trim(),
        predicate: { field, op, value: coerced },
        action,
        reason: reason.trim() || null,
      });
      toast.success('Rule saved');
      setLabel(''); setValue(''); setReason('');
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to save rule');
    }
  };

  const toggle = async (rule) => {
    try {
      await api.patch(`/rules/${rule.id}`, { active: !rule.active });
      load();
    } catch (e) {
      toast.error('Toggle failed');
    }
  };

  const del = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.label}"?`)) return;
    try {
      await api.delete(`/rules/${rule.id}`);
      toast.success('Rule deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="rules-page">
      <header className="flex items-center gap-3">
        <div className="rounded-xl bg-orange-100 p-2 border border-orange-200">
          <Filter className="text-orange-600" size={22} />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Rules</h1>
          <p className="text-sm text-slate-600">
            Permanent rules Zita applies to every scan. Every result they hide is auditable.
          </p>
        </div>
      </header>

      <form
        onSubmit={save}
        className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-3"
        data-testid="rule-create-form"
      >
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">New rule</div>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Give it a name (e.g. 'No expensive dryers')"
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
          data-testid="rule-label-input"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select
            value={field}
            onChange={(e) => setField(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="rule-field-select"
          >
            {FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          <select
            value={op}
            onChange={(e) => setOp(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="rule-op-select"
          >
            {(OPS_BY_FIELD[field] || []).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input
            type={field === 'latest_price' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={field === 'latest_price' ? '500' : field === 'source_key' ? 'craigslist' : 'parts only'}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="rule-value-input"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="rule-action-select"
          >
            {ACTIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why? (optional — shown when this rule hides something)"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="rule-reason-input"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700"
          data-testid="rule-save-btn"
        >
          <Plus size={14} /> Save rule
        </button>
      </form>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Active rules</div>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : rules.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500"
               data-testid="rules-empty">
            No rules yet. Rules run automatically on every future scan.
          </div>
        ) : (
          <div className="space-y-2" data-testid="rules-list">
            {rules.map((r) => (
              <div key={r.id}
                   className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3"
                   data-testid={`rule-row-${r.id}`}>
                <button
                  onClick={() => toggle(r)}
                  className={r.active ? 'text-emerald-600' : 'text-slate-400'}
                  title={r.active ? 'Active — click to pause' : 'Paused — click to activate'}
                  data-testid={`rule-toggle-${r.id}`}
                >
                  {r.active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{r.label}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.predicate?.field} {r.predicate?.op} <b>{String(r.predicate?.value)}</b> · then <b>{r.action}</b>
                    {r.reason && <> · <i>{r.reason}</i></>}
                  </div>
                </div>
                <button
                  onClick={() => del(r)}
                  className="text-slate-400 hover:text-red-600"
                  title="Delete"
                  data-testid={`rule-delete-${r.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Rules;
